/**
 * LỊCH CA — week view.
 *
 * A week is the right window: it is what a restaurant rota is published in, and
 * it fits on a phone without horizontal scrolling. The seven day columns are
 * rendered as seven rows instead of a grid, because a 7-column grid on a 360px
 * screen gives each day 51px — below the 48px touch minimum once padding is
 * accounted for.
 *
 * Week starts on Monday: `days_of_week_mask` in the database uses bit 0 =
 * Monday, and a rota that starts on a different day than the data model is a
 * permanent source of off-by-one bugs.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShifts, errorMessage } from '../lib/queries';
import { formatMinutes, formatTime, toDateInput, weekdayShort } from '../lib/format';
import { approvalStatusLabel, approvalStatusTone } from '../lib/labels';
import type { StaffShift } from '../lib/contracts';
import { Button, Card, EmptyState, ErrorState, LoadingState, StatusBadge } from '../components/ui';
import { ScreenHeader } from '../components/ScreenHeader';

/** Monday of the week containing `date`. */
function mondayOf(date: Date): Date {
  const d = new Date(date);
  const offset = (d.getDay() + 6) % 7; // 0 = Monday
  d.setDate(d.getDate() - offset);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function ShiftScheduleScreen(): JSX.Element {
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState<Date>(() => mondayOf(new Date()));

  const days = useMemo(() => {
    return Array.from({ length: 7 }, (_, index) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + index);
      return toDateInput(d);
    });
  }, [weekStart]);

  const from = days[0] ?? toDateInput(weekStart);
  const to = days[6] ?? from;
  const { data, isLoading, isError, error, refetch } = useShifts(from, to);

  const byDate = useMemo(() => {
    const map = new Map<string, StaffShift[]>();
    for (const shift of data?.shifts ?? []) {
      const list = map.get(shift.businessDate) ?? [];
      list.push(shift);
      map.set(shift.businessDate, list);
    }
    return map;
  }, [data]);

  const today = toDateInput(new Date());
  const shiftWeek = (weeks: number): void => {
    setWeekStart((prev) => {
      const next = new Date(prev);
      next.setDate(next.getDate() + weeks * 7);
      return next;
    });
  };

  const totalMinutes = (data?.shifts ?? []).reduce(
    (sum, shift) => sum + Math.max(0, Math.round((shift.endAt - shift.startAt) / 60_000)) - shift.breakMinutes,
    0,
  );

  return (
    <div className="pb-8">
      <ScreenHeader title="Lịch ca" subtitle={`${from} → ${to}`} />

      <div className="mb-3 flex items-center gap-2">
        <Button variant="secondary" onClick={() => shiftWeek(-1)}>
          ‹ Tuần trước
        </Button>
        <Button variant="secondary" className="flex-1" onClick={() => setWeekStart(mondayOf(new Date()))}>
          Tuần này
        </Button>
        <Button variant="secondary" onClick={() => shiftWeek(1)}>
          Tuần sau ›
        </Button>
      </div>

      {isLoading ? <LoadingState /> : null}
      {isError ? <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} /> : null}

      {data === undefined ? null : (
        <>
          <Card className="mb-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Tổng giờ làm dự kiến trong tuần</span>
              <span className="text-lg font-bold tabular-nums">{formatMinutes(totalMinutes)}</span>
            </div>
          </Card>

          <ul className="space-y-2">
            {days.map((date) => {
              const shifts = byDate.get(date) ?? [];
              const isToday = date === today;
              return (
                <li key={date}>
                  <Card className={isToday ? 'ring-2 ring-brand-400' : ''}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-baseline gap-2">
                        <span className="w-8 text-sm font-bold text-slate-500">{weekdayShort(date)}</span>
                        <span className="text-base font-semibold text-slate-900">{date.slice(8)}/{date.slice(5, 7)}</span>
                        {isToday ? <StatusBadge tone="info">Hôm nay</StatusBadge> : null}
                      </div>
                      {shifts.length === 0 ? <span className="text-sm text-slate-400">Nghỉ</span> : null}
                    </div>

                    {shifts.map((shift) => (
                      <div key={shift.assignmentId} className="mt-2 rounded-xl bg-slate-50 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold tabular-nums text-slate-900">
                            {formatTime(shift.startAt)} – {formatTime(shift.endAt)}
                          </span>
                          <StatusBadge tone={approvalStatusTone(shift.assignmentStatus)}>
                            {approvalStatusLabel(shift.assignmentStatus)}
                          </StatusBadge>
                        </div>
                        <p className="mt-1 text-sm text-slate-600">
                          {shift.name ?? 'Ca làm việc'}
                          {shift.isShiftLead ? ' · Trưởng ca' : ''}
                          {shift.breakMinutes > 0 ? ` · nghỉ ${formatMinutes(shift.breakMinutes)}` : ''}
                        </p>
                      </div>
                    ))}
                  </Card>
                </li>
              );
            })}
          </ul>

          {(data.shifts.length === 0) ? (
            <div className="mt-3">
              <EmptyState title="Tuần này bạn chưa được xếp ca" description="Liên hệ quản lý chi nhánh nếu bạn nghĩ đây là nhầm lẫn." />
            </div>
          ) : null}

          <div className="mt-4 grid grid-cols-2 gap-3">
            <Button variant="secondary" size="lg" onClick={() => navigate('/ca-nhan/xin-nghi')}>
              Xin nghỉ
            </Button>
            <Button variant="secondary" size="lg" onClick={() => navigate('/ca-nhan/doi-ca')}>
              Đổi ca
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
